import { Box, Typography, Chip, Divider } from "@mui/material";
import {
  EllipsisVertical,
  ClipboardPen,
  ClipboardCheck,
  Cpu,
  ListTodo,
} from "lucide-react";
import { Document, DocumentScope, DocumentType } from "protogen/documents_pb";
import useIsMobile from "components/hooks/useIsMobile";
import DateDisplay from "components/common/DateDisplay";
import {
  getDocumentLink,
  getScopeGroupLabel,
  isFormDefinitionDocument,
  isListDocument,
  isManagedBySmsBot,
  isPrivate,
} from "./utils";
import EmptyTableOverlay from "components/common/EmptyTableOverlay";
import DropdownMenu from "components/common/DropdownMenu";
import LinkRouter from "components/navigation/LinkRouter";
import { DocumentFavoriteIcon } from "./DocumentFavoriteIcon";
import FlexBox from "components/common/FlexBox";
import { CurrentUser } from "protogen/auth_pb";
import { useContext } from "react";
import { CurrentUserContext } from "components/context/RequireAuth";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";

const FixedColumnWidth = "120px";

const VisibilityChip = ({ document }: { document: Document }) => {
  const scopeLabel =
    document.documentType === DocumentType.TASK_BLOCK
      ? "Shared"
      : getScopeGroupLabel(document.scope);

  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>{scopeLabel}</Box>
      }
      variant={isPrivate(document) ? "outlined" : "green"}
      sx={{
        width: "85px",
      }}
    />
  );
};

const DocumentActionMenu = ({
  document,
  onDuplicate,
  onDelete,
  showDelete = true,
}: {
  document: Document;
  onDuplicate: (document: Document) => void;
  onDelete: (document: Document) => void;
  showDelete?: boolean;
}) => {
  const options = [
    {
      title: "Duplicate",
      onClick: () => onDuplicate(document),
    },
  ];

  if (showDelete) {
    options.push({
      title: "Delete",
      onClick: () => onDelete(document),
    });
  }

  return (
    <DropdownMenu
      key={document.ref}
      disabled={false}
      fullWidth
      buttonContent={<EllipsisVertical size={20} color="#8E9598" />}
      dropdownIcon={false}
      variant="text"
      options={options}
      sx={{
        minWidth: "auto",
      }}
    />
  );
};

const TableHeader = ({
  clientView = false,
  title,
}: {
  clientView: boolean;
  title?: string;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        height: "32px",
      }}
    >
      <Typography
        variant={title ? "h4" : "bodySmallHeavy"}
        color={title ? "text.primary" : "text.secondary"}
        sx={{ width: "1%", flexGrow: 1 }}
      >
        {title || "Name"}
      </Typography>
      {!clientView && (
        <>
          <Typography
            variant="bodySmallHeavy"
            color="text.secondary"
            sx={{ width: FixedColumnWidth, textAlign: "end" }}
          >
            Owner
          </Typography>
          <Typography
            variant="bodySmallHeavy"
            color="text.secondary"
            sx={{ width: FixedColumnWidth, textAlign: "center" }}
          >
            Visibility
          </Typography>
        </>
      )}
      <Typography
        variant="bodySmallHeavy"
        color="text.secondary"
        sx={{
          width: clientView ? "auto" : FixedColumnWidth,
          textAlign: "center",
        }}
      >
        Updated
      </Typography>
    </Box>
  );
};

const getDocOwner = (document: Document, currentUser: CurrentUser) => {
  const isAdmin = document.scope === DocumentScope.ADMIN;
  const owner = document.family?.name || (isAdmin ? "Faye" : "Me");
  if (document.family?.name) {
    return owner;
  } else if (document.scope === DocumentScope.ADMIN) {
    return "Faye";
  } else if (document.owner?.ref === currentUser.ref) {
    return "Me";
  } else {
    return (
      <LinkRouter
        inline={true}
        to={`/advisor/${encodeURIComponent(document.owner?.ref || "")}`}
        sx={{
          textDecoration: "none",
          color: "#198282",
          fontWeight: "600",
          textAlign: "end",
        }}
      >
        {document.owner?.displayName}
      </LinkRouter>
    );
  }
};

const DocumentRow = ({
  document,
  onDuplicate,
  onDelete,
  onFavoriteUpdate,
  showFavoriteCount = false,
  layout = "detailed",
  clientView = false,
}: {
  document: Document;
  onDuplicate: (document: Document) => void;
  onDelete: (document: Document) => void;
  onFavoriteUpdate?: () => void;
  showFavoriteCount?: boolean;
  layout?: "simple" | "detailed";
  clientView?: boolean;
}) => {
  const isFormDefinition = isFormDefinitionDocument(document);
  const isFormResponse =
    document.documentType === DocumentType.FORM && !isFormDefinition;
  const isList = isListDocument(document);
  const isSmsBot = isManagedBySmsBot(document);
  const formSubPath = isFormDefinition ? "create" : "complete";
  const currentUser = useContext(CurrentUserContext);
  const owner = getDocOwner(document, currentUser);
  const isCreatedByCurrentUser = document.owner?.ref === currentUser.ref;
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        padding: "12px 0",
      }}
    >
      <FlexBox gap="12px" sx={{ flex: 1, minWidth: 0 }}>
        {layout === "detailed" && (
          <DocumentFavoriteIcon
            document={document}
            showCount={showFavoriteCount}
            size="small"
            showTooltip={false}
            isStacked={true}
            sx={{ marginTop: "2px" }}
            onUpdate={onFavoriteUpdate}
          />
        )}

        <FlexBox
          direction="column"
          gap="0"
          sx={{ alignSelf: "center", flex: 1, minWidth: 0 }}
        >
          {/* Document title */}
          <FlexBox
            sx={{
              flex: 1,
              minWidth: 0,
              flexDirection: clientView ? "column" : "row",
              alignItems: clientView ? "start" : "center",
              gap: clientView ? "0" : "8px",
              minHeight: clientView ? "40px" : "auto",
              justifyContent: clientView ? "center" : "",
            }}
          >
            {/* Unread indicator - defaulted to true for now */}
            {document.unreadCount > 0 && (
              <EllipseIcon height={8} width={8} style={{ minWidth: "8px" }} />
            )}
            <LinkRouter
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: 0,
                minWidth: 0,
              }}
              to={
                document.documentType === DocumentType.FORM
                  ? `/forms/${formSubPath}/${encodeURIComponent(document.uuid)}`
                  : getDocumentLink(document)
              }
              key={document.ref}
            >
              <Typography
                variant="bodyHeavy"
                color="text.primary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {document.title || "Untitled"}
              </Typography>
              {isFormDefinition && <ClipboardPen size={16} color="#8E9598" />}
              {isFormResponse && <ClipboardCheck size={16} color="#8E9598" />}
              {isList && <ListTodo size={16} color="#8E9598" />}
              {isSmsBot && <Cpu size={16} color="#8E9598" />}
            </LinkRouter>

            {document.documentType === DocumentType.TASK_BLOCK &&
              document.subtitle && (
                <LinkRouter
                  to={`/tasks/${encodeURIComponent(
                    document.ref || "",
                  )}?blockRef=${encodeURIComponent(
                    document?.taskBlock?.ref || "",
                  )}`}
                  sx={{ padding: 0, textDecoration: "none", minWidth: 0 }}
                >
                  <Typography
                    variant="bodySmall"
                    color="primary"
                    sx={{
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                    }}
                  >
                    {document.subtitle}
                  </Typography>
                </LinkRouter>
              )}
          </FlexBox>

          {/* Topics */}
          {!clientView && document.topics?.length > 0 && (
            <FlexBox gap="4px">
              {document.topics.map((topic) => (
                <LinkRouter
                  to={`/documents/community/${encodeURIComponent(
                    topic.trim().toLowerCase(),
                  )}`}
                  sx={{ padding: 0 }}
                  key={topic}
                >
                  <Typography variant="bodySmall" color="text.secondary">
                    {topic}
                  </Typography>
                </LinkRouter>
              ))}
            </FlexBox>
          )}
        </FlexBox>
      </FlexBox>

      {!clientView && (
        <>
          <Typography
            variant="bodySmall"
            color="text.primary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: FixedColumnWidth,
              textAlign: "end",
            }}
          >
            {owner}
          </Typography>

          <Box
            sx={{
              display: "flex",
              width: FixedColumnWidth,
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <VisibilityChip document={document} />
          </Box>
        </>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          width: FixedColumnWidth,
          justifyContent: "flex-end",
          gap: "24px",
        }}
      >
        <DateDisplay
          date={new Date(Number(document.lastUpdatedSec) * 1000)}
          useRelativeAsPrimary={false}
        />
        {layout === "detailed" && (
          <DocumentActionMenu
            document={document}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            showDelete={isCreatedByCurrentUser && !isSmsBot}
          />
        )}
      </Box>
    </Box>
  );
};

const DocumentRowMobile = ({
  document,
  showFavoriteCount,
  onFavoriteUpdate,
  layout = "detailed",
  clientView = false,
}: {
  document: Document;
  showFavoriteCount: boolean;
  onFavoriteUpdate?: () => void;
  layout?: "simple" | "detailed";
  clientView?: boolean;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const isFormDefinition = isFormDefinitionDocument(document);
  const isFormResponse =
    document.documentType === DocumentType.FORM && !isFormDefinition;
  const isList = isListDocument(document);
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        padding: "12px 0px",
        alignItems: "start",
      }}
    >
      {layout === "detailed" && (
        <DocumentFavoriteIcon
          document={document}
          showCount={showFavoriteCount}
          onUpdate={onFavoriteUpdate}
        />
      )}
      <FlexBox gap="0" direction="column" sx={{ flex: 1, minWidth: 0 }}>
        <FlexBox
          sx={{
            flex: 1,
            minWidth: 0,
            flexDirection: clientView ? "column" : "row",
            alignItems: clientView ? "start" : "center",
            gap: clientView ? "0" : "8px",
            justifyContent: clientView ? "center" : "",
          }}
        >
          <FlexBox
            justifyContent="space-between"
            flex={1}
            sx={{ width: "100%", minWidth: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                minWidth: 0,
                flex: 1,
              }}
            >
              {/* Unread indicator - defaulted to true for now */}
              {document.unreadCount > 0 && (
                <EllipseIcon height={8} width={8} style={{ minWidth: "8px" }} />
              )}
              <Typography
                variant="bodyHeavy"
                color="text.primary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {document.title || "Untitled"}
              </Typography>
              {isFormDefinition && (
                <ClipboardPen
                  size={16}
                  color="#8E9598"
                  style={{ flexShrink: 0 }}
                />
              )}
              {isFormResponse && (
                <ClipboardCheck
                  size={16}
                  color="#8E9598"
                  style={{ flexShrink: 0 }}
                />
              )}
              {isList && (
                <ListTodo size={16} color="#8E9598" style={{ flexShrink: 0 }} />
              )}
            </Box>
            {clientView && (
              <DateDisplay
                date={new Date(Number(document.lastUpdatedSec) * 1000)}
                useRelativeAsPrimary={false}
                sx={{ flexShrink: 0 }}
              />
            )}
          </FlexBox>
          {/* Task name for TaskBlock documents */}
          {document.documentType === DocumentType.TASK_BLOCK &&
            document.subtitle && (
              <Typography
                variant="body"
                color="primary"
                sx={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {document.subtitle}
              </Typography>
            )}
        </FlexBox>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {!clientView && (
              <>
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getDocOwner(document, currentUser)}
                </Typography>
                <Typography variant="body" color="text.secondary">
                  •
                </Typography>
                <DateDisplay
                  date={new Date(Number(document.lastUpdatedSec) * 1000)}
                  useRelativeAsPrimary={false}
                  sx={{ flexShrink: 0 }}
                />
              </>
            )}
          </Box>
          {!clientView && <VisibilityChip document={document} />}
        </Box>
      </FlexBox>
    </Box>
  );
};

type Props = {
  documents: Document[];
  loading?: boolean;
  onDuplicate?: (document: Document) => void;
  onDelete?: (document: Document) => void;
  onFavoriteUpdate?: () => void;
  showFavoriteCount?: boolean;
  layout?: "simple" | "detailed";
  formResults?: boolean;
  clientView?: boolean;
  title?: string;
};

export default ({
  documents,
  loading,
  onDuplicate,
  onDelete,
  onFavoriteUpdate,
  showFavoriteCount = false,
  layout = "detailed",
  clientView = false,
  title,
}: Props) => {
  const isMobile = useIsMobile();
  // Move unread docs to the front of the list
  const unreadDocuments = documents.filter(
    (document) => document.unreadCount > 0,
  );
  const readDocuments = documents.filter(
    (document) => document.unreadCount === 0,
  );
  const allDocuments = [...unreadDocuments, ...readDocuments];
  return (
    <Box>
      {!isMobile && (
        <Box>
          <TableHeader clientView={clientView} title={title} />
          <Divider />
          {allDocuments.map((document) => {
            const isFormDefinition = isFormDefinitionDocument(document);
            const formSubPath = isFormDefinition ? "create" : "complete";
            let linkTo = getDocumentLink(document);
            if (document.documentType === DocumentType.TASK_BLOCK) {
              linkTo = `/tasks/${encodeURIComponent(
                document.ref || "",
              )}?blockRef=${encodeURIComponent(
                document?.taskBlock?.ref || "",
              )}`;
            } else if (document.documentType === DocumentType.FORM) {
              linkTo = `/forms/${formSubPath}/${encodeURIComponent(
                document.uuid,
              )}`;
            }
            const row = (
              <DocumentRow
                document={document}
                onDuplicate={onDuplicate || (() => {})}
                onDelete={onDelete || (() => {})}
                onFavoriteUpdate={onFavoriteUpdate}
                showFavoriteCount={showFavoriteCount}
                layout={layout}
                clientView={clientView}
              />
            );
            return (
              <Box
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
                key={`${document.ref}-${document?.taskBlock?.ref}`}
              >
                {clientView ? <LinkRouter to={linkTo}>{row}</LinkRouter> : row}
                {/* Only show divider if not the last document in the list */}
                {document.ref !==
                  allDocuments[allDocuments.length - 1]?.ref && <Divider />}
              </Box>
            );
          })}
        </Box>
      )}
      {isMobile && (
        <Box>
          {allDocuments.map((document) => {
            const isFormDefinition = isFormDefinitionDocument(document);
            const formSubPath = isFormDefinition ? "create" : "complete";
            let linkTo = getDocumentLink(document);
            if (document.documentType === DocumentType.TASK_BLOCK) {
              linkTo = `/tasks/${encodeURIComponent(
                document.ref || "",
              )}?blockRef=${encodeURIComponent(
                document?.taskBlock?.ref || "",
              )}`;
            } else if (document.documentType === DocumentType.FORM) {
              linkTo = `/forms/${formSubPath}/${encodeURIComponent(
                document.uuid,
              )}`;
            }

            return (
              <Box
                sx={{ width: "100%", display: "flex", flexDirection: "column" }}
                key={`${document.ref}-${document?.taskBlock?.ref}`}
              >
                <LinkRouter to={linkTo}>
                  <DocumentRowMobile
                    document={document}
                    showFavoriteCount={showFavoriteCount}
                    onFavoriteUpdate={onFavoriteUpdate}
                    layout={layout}
                    clientView={clientView}
                  />
                </LinkRouter>
                {/* Only show divider if not the last document in the list */}
                {document.ref !==
                  allDocuments[allDocuments.length - 1]?.ref && <Divider />}
              </Box>
            );
          })}
        </Box>
      )}
      {(loading || allDocuments.length === 0) && (
        <Box sx={{ marginTop: "24px" }}>
          <EmptyTableOverlay title="No documents...yet" />
        </Box>
      )}
    </Box>
  );
};
